import React, { useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  CompareArrowsRounded as LoanIcon,
  ReceiptLongRounded as DebtIcon,
} from '@mui/icons-material';
import { useBankAccounts } from './hooks/useBankAccounts';
import { useCustomers } from './hooks/useCustomers';
import { useLoanActualBalance } from './hooks/useLoanActualBalance';
import LoanPairSummaryCard from './components/cards/LoanPairSummaryCard';
import DebtNoteList from './components/lists/DebtNoteList';

// แถบแท็บ — ดีไซน์ pill ไล่สีเหมือนหน้า /finance/expenses (ExpensesList.js) ก็อป style มาตรงๆ ไม่ได้ share component
const TABS = [
  { label: 'ยืม-คืน', icon: LoanIcon, gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' },
  { label: 'เจ้าหนี้ / ลูกหนี้', icon: DebtIcon, gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' },
];

// แท็บ "เจ้าหนี้ / ลูกหนี้" = บันทึกจดไว้เฉยๆ ไม่ผูกกับคู่ยืม-คืน (ดู DebtNoteList.js) แสดง 2 คอลัมน์คู่กัน
const NOTE_CARDS = [
  { category: 'CREDITOR', title: 'เจ้าหนี้', hint: 'เราเป็นหนี้', partyLabel: 'เจ้าหนี้' },
  { category: 'DEBTOR', title: 'ลูกหนี้', hint: 'เป็นหนี้เรา', partyLabel: 'ลูกหนี้' },
];

// ชื่อรายการในการ์ดเจ้าหนี้ที่แสดงยอดคงเหลือ (ตามจริง) ตั้งได้ที่หน้า "ตั้งค่าคู่ยืม-คืน" แท็บ "ตั้งค่า" (loan_balance_label) — ไม่ตั้ง = ข้อความนี้
const DEFAULT_LOAN_BALANCE_LABEL = 'ยืมเงิน';

// หน้า "ยืม-คืน" หลัก — เดิมมีทั้ง header banner + ปุ่มสร้าง/แก้ไขคู่บัญชี + การ์ดสรุป/
// ประวัติ ตอนนี้เหลือแค่ดูข้อมูลของคู่หลัก (bankAccounts[0]) อย่างเดียว ไม่มี header เลย
// (ตัดตามที่ผู้ใช้ขอ) ส่วนกำหนด/แก้ไขบัญชีของคู่ยืม-คืน (สร้างคู่ใหม่, สลับ Customer,
// รองรับหลายคู่) ย้ายไปหน้า /accounting/loan-pairs-setup แทนแล้ว (LoanPairSetup.js) —
// หน้านี้ใช้บ่อยกว่ามาก เลยให้เข้าถึงตรงๆ ไม่ต้องเจอ header/ปุ่มจัดการที่ไม่ได้ใช้บ่อย
export function LoanPairs() {
  const { bankAccounts, loading, reload } = useBankAccounts();
  const { customerName } = useCustomers();
  const mainPair = bankAccounts.find((account) => account.mode === 'BALANCE');
  const [tab, setTab] = useState(0);
  // ยอดคงเหลือ (ตามจริง) ของคู่ยืม-คืน — แสดงเป็นรายการพิเศษในการ์ดเจ้าหนี้ (ดึงใหม่ทุกครั้งที่สลับแท็บ)
  const actualBalance = useLoanActualBalance(mainPair, tab);

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 2 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 0.75,
            p: 0.75,
            borderRadius: '18px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: (theme) => (theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0,0,0,0.35)'
              : '0 4px 20px rgba(15,23,42,0.06)'),
          }}
        >
          {TABS.map((t, idx) => {
            const selected = tab === idx;
            const Icon = t.icon;
            return (
              <Box
                key={t.label}
                onClick={() => setTab(idx)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.9,
                  px: 2,
                  py: 1,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'transform 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease',
                  transform: selected ? 'translateY(-1px)' : 'none',
                  background: selected ? t.gradient : 'transparent',
                  boxShadow: selected ? '0 6px 16px -4px rgba(0,0,0,0.35)' : 'none',
                  '&:hover': {
                    background: selected
                      ? t.gradient
                      : (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)'),
                  },
                }}
              >
                <Icon sx={{ fontSize: 19, color: selected ? '#fff' : 'text.secondary' }} />
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: selected ? 700 : 500,
                    color: selected ? '#fff' : 'text.secondary',
                  }}
                >
                  {t.label}
                </Typography>
              </Box>
            );
          })}
        </Paper>
      </Box>

      {tab === 0 && (loading ? null : !mainPair ? (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, borderStyle: 'dashed', textAlign: 'center', bgcolor: '#fafafa' }}>
          <Typography variant="h6" sx={{ fontWeight: 750 }}>ยังไม่มีคู่ยืม-คืน</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            ไปที่เมนู &quot;ตั้งค่าคู่ยืม-คืน&quot; เพื่อกำหนดบัญชีฝั่งยืมและฝั่งคืนก่อน
          </Typography>
        </Paper>
      ) : (
        <LoanPairSummaryCard pair={mainPair} customerName={customerName} onSaved={reload} />
      ))}

      {tab === 1 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5, alignItems: 'start' }}>
          {NOTE_CARDS.map((c) => (
            <DebtNoteList
              key={c.category}
              category={c.category}
              title={c.title}
              hint={c.hint}
              partyLabel={c.partyLabel}
              loanBalanceRow={c.category === 'CREDITOR' && actualBalance != null
                ? { party: mainPair?.loan_balance_label || DEFAULT_LOAN_BALANCE_LABEL, amount: actualBalance }
                : null}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default LoanPairs;
